import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { isMobile } from '../helpers/HelperFunctions.js';

const CropControl = (props) => {
    const zoomMaxValue = 4;

    const [mountPoint, setMountPoint] = useState(null);
    const [position, setPosition] = useState({ leftOffset: 0, topOffset: 0, zoom: 1 });

    const rootElementRef = useRef(null);
    const cropContentRef = useRef(null);
    const cropImageRef = useRef(null);
    const cropElementRef = useRef(null);

    const mountPointRef = useRef(mountPoint);
    const positionRef = useRef(position);

    useEffect(() => {
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', stopDrag);

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', stopDrag);
        }
    }, []);

    useEffect(() => {
        if (props.imageUrl) {
            const image = new Image();
            image.onload = () => {
                const newPosition = { leftOffset: 0, topOffset: 0, zoom: 1 };
                newPosition.size = {
                    width: image.naturalWidth,
                    height: image.naturalHeight
                };
                setPosition(newPosition);
            };
            image.src = props.imageUrl;
        }
    }, [props.imageUrl]);

    useEffect(() => {
        if (position.size) {
            positionRef.current = position;
            cropReposition();
            if (props.onChange) {
                const cropSize = cropElementRef.current.getBoundingClientRect();
                const imgSize = cropImageRef.current.getBoundingClientRect();

                const dx = position.leftOffset / 100 * imgSize.width / position.zoom;
                const dxInit = (imgSize.width - cropSize.width) / 2.;
                const x = dxInit - dx;

                const dy = position.topOffset / 100 * imgSize.height / position.zoom;
                const dyInit = (imgSize.height - cropSize.height) / 2.;
                const y = dyInit - dy;


                props.onChange({
                    size: Math.min(position.size.width, position.size.height) / position.zoom,
                    leftOffset: x / imgSize.width,
                    topOffset: y / imgSize.height
                });
            }
        }
    }, [position]);

    useEffect(() => {
        mountPointRef.current = mountPoint;
    }, [mountPoint]);


    const cropReposition = () => {
        const maxWidth = rootElementRef.current.getBoundingClientRect().width;
        const maxHeight = 300;
        const scaleX = maxWidth / position.size.width;
        const scaleY = maxHeight / position.size.height;
        const scale = Math.min(scaleX, scaleY);
        cropContentRef.current.style.width = `${(position.size.width * scale).toFixed(2)}px`;
        cropContentRef.current.style.height = `${(position.size.height * scale).toFixed(2)}px`;

        cropImageRef.current.style.transform = `scale(${position.zoom.toFixed(2)})`;
        cropImageRef.current.style.left = `${position.leftOffset.toFixed(2)}%`;
        cropImageRef.current.style.top = `${position.topOffset.toFixed(2)}%`;

        if (position.size.height > position.size.width) {
            cropElementRef.current.style.width = '100%';
            cropElementRef.current.style.height = `${(position.size.width / position.size.height * 100).toFixed(2)}%`;
        }
        else {
            cropElementRef.current.style.height = '100%';
            cropElementRef.current.style.width = `${(position.size.height / position.size.width * 100).toFixed(2)}%`;
        }
    }

    const onZoomChanged = (e) => {
        const newPosition = structuredClone(position);
        const value = e.currentTarget.value;
        const zoom = (value / 100) * (zoomMaxValue - 1) + 1;
        const relativeZoom = position.zoom / zoom;


        newPosition.leftOffset *= relativeZoom;
        newPosition.topOffset *= relativeZoom;
        newPosition.zoom = zoom;

        const limits = getLimits(zoom);
        if (newPosition.leftOffset < limits.minLeft) { newPosition.leftOffset = limits.minLeft; }
        if (newPosition.leftOffset > limits.maxLeft) { newPosition.leftOffset = limits.maxLeft; }
        if (newPosition.topOffset < limits.minTop) { newPosition.topOffset = limits.minTop; }
        if (newPosition.topOffset > limits.maxTop) { newPosition.topOffset = limits.maxTop; }

        setPosition(newPosition);
    }

    const startDrag = (e) => {
        e.stopPropagation();
        if (e.type === 'touchstart') {
            e = e.touches[0];
        }
        const mountPoint = { x: e.pageX, y: e.pageY, left: position.leftOffset, top: position.topOffset };
        setMountPoint(mountPoint);
        if (isMobile()) {
            const mainWindow = document.querySelector('.main-content');
            if (mainWindow) {
                mainWindow.style.overflow = 'hidden';
            }
        }
    }

    const stopDrag = (e) => {
        e.stopPropagation();
        setMountPoint(null);
        if (isMobile()) {
            const mainWindow = document.querySelector('.main-content');
            if (mainWindow) {
                mainWindow.style.overflow = 'auto';
            }
        }
    }

    const onMouseMove = (e) => {
        if (mountPointRef.current) {
            if (e.type == 'touchmove') {
                e = e.touches[0];
            }

            const cropFullSize = cropContentRef.current.getBoundingClientRect();

            const limits = getLimits();

            const xDistance = e.pageX - mountPointRef.current.x;
            const xOffset = xDistance / cropFullSize.width * 100;

            let leftOffset = mountPointRef.current.left + xOffset;
            if (leftOffset < limits.minLeft) leftOffset = limits.minLeft;
            if (leftOffset > limits.maxLeft) leftOffset = limits.maxLeft;

            const yDistance = e.pageY - mountPointRef.current.y;
            const yOffset = yDistance / cropFullSize.height * 100;

            let topOffset = mountPointRef.current.top + yOffset;
            if (topOffset < limits.minTop) topOffset = limits.minTop;
            if (topOffset > limits.maxTop) topOffset = limits.maxTop;

            const newPosition = structuredClone(positionRef.current);
            newPosition.leftOffset = leftOffset;
            newPosition.topOffset = topOffset;
            setPosition(newPosition);
        }
    }

    const getLimits = (newZoom = null) => {
        if (newZoom) {
            cropImageRef.current.style.transform = `scale(${newZoom.toFixed(2)})`;
        }

        const cropFullSize = cropContentRef.current.getBoundingClientRect();
        const cropSize = cropElementRef.current.getBoundingClientRect();
        const imgSize = cropImageRef.current.getBoundingClientRect();

        let dX = (imgSize.width - cropSize.width) / 2.0 / cropFullSize.width * 100;
        let dY = (imgSize.height - cropSize.height) / 2.0 / cropFullSize.height * 100;

        let minLeft = -dX;
        let maxLeft = dX;
        let minTop = -dY;
        let maxTop = dY;

        return { minLeft, maxLeft, minTop, maxTop };
    }

    return (<div className="crop-control" ref={rootElementRef} >
        <div className="crop-content" ref={cropContentRef} onMouseDown={startDrag} onTouchStart={startDrag} onTouchMove={onMouseMove} onTouchEnd={stopDrag} >
            <img className="crop-img" src={props.imageUrl} ref={cropImageRef} />
            <div className="crop" ref={cropElementRef} ></div>
        </div>
        <Form.Range value={(position.zoom - 1) / (zoomMaxValue - 1) * 100} onChange={onZoomChanged} />
    </div>);
}

export default CropControl;